// src/pages/guide/FasoorGuide.tsx

import React from "react";
import { Link, useNavigate } from "react-router-dom";

import "./FasoorGuide.css";

// =====================================================
// TYPES
// =====================================================

type Suit =
  | "spades"
  | "hearts"
  | "diamonds"
  | "clubs";

type Rank =
  | "ace"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "jack"
  | "queen"
  | "king";

interface PlayingCardProps {
  rank: Rank;
  suit: Suit;
  highlight?: boolean;
  label?: string;
}

interface ScoreItem {
  value: string;
  title: string;
  description: string;
}

// =====================================================
// CARD ASSETS
// =====================================================
//
// Actual files:
// frontend/public/cards/
//
// Browser examples:
// /cards/10_of_diamonds.png
// /cards/7_of_clubs.png
// /cards/king_of_spades.png
//
// =====================================================

const getCardImage = (
  rank: Rank,
  suit: Suit
): string => {
  return `/cards/${rank}_of_${suit}.png`;
};

// =====================================================
// CARD COMPONENT
// =====================================================

const PlayingCard: React.FC<PlayingCardProps> = ({
  rank,
  suit,
  highlight = false,
  label,
}) => {
  return (
    <div
      className={[
        "fasoor-guide-card",
        highlight ? "fasoor-guide-card-highlight" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <img
        src={getCardImage(rank, suit)}
        alt={`${rank} of ${suit}`}
        draggable={false}
      />

      {label && (
        <span className="fasoor-guide-card-label">
          {label}
        </span>
      )}
    </div>
  );
};

// =====================================================
// SYMBOLS
// =====================================================

const Plus = () => (
  <span
    className="fasoor-guide-symbol"
    aria-hidden="true"
  >
    +
  </span>
);

const Arrow = () => (
  <span
    className="fasoor-guide-symbol fasoor-guide-arrow"
    aria-hidden="true"
  >
    →
  </span>
);

// =====================================================
// SCORE DATA
// =====================================================

const SCORE_ITEMS: ScoreItem[] = [
  {
    value: "10",
    title: "Soor",
    description:
      "A valid capture that clears the table earns 10 points.",
  },
  {
    value: "1",
    title: "Jack Capture",
    description:
      "A successful Jack capture earns 1 point.",
  },
  {
    value: "3",
    title: "10♦",
    description:
      "The 10 of Diamonds is worth 3 points when collected.",
  },
  {
    value: "2",
    title: "2♣",
    description:
      "The 2 of Clubs is worth 2 points when collected.",
  },
  {
    value: "1",
    title: "Ace",
    description:
      "Each collected Ace is worth 1 point.",
  },
  {
    value: "17",
    title: "Seven Clubs",
    description:
      "Collecting seven Clubs during a round earns 17 bonus points.",
  },
];

// =====================================================
// FASOOR GUIDE
// =====================================================

const FasoorGuide: React.FC = () => {
  const navigate = useNavigate();

  const playFasoor = () => {
    navigate("/lobby");
  };

  return (
    <main className="fasoor-guide-page">

      {/* ================================================= */}
      {/* HERO                                              */}
      {/* ================================================= */}

      <section className="fasoor-guide-hero">

        <div className="fasoor-guide-hero-glow" />

        <div className="fasoor-guide-hero-content">

          <nav
            className="fasoor-guide-breadcrumb"
            aria-label="Breadcrumb"
            >
            <Link
                to="/"
                className="fasoor-guide-breadcrumb-home"
            >
                P-ARENA
            </Link>

            <span
                className="fasoor-guide-breadcrumb-separator"
                aria-hidden="true"
            >
                /
            </span>

            <span className="fasoor-guide-breadcrumb-category">
                Game Guides
            </span>

            <span
                className="fasoor-guide-breadcrumb-separator"
                aria-hidden="true"
            >
                /
            </span>

            <strong
                className="fasoor-guide-breadcrumb-current"
                aria-current="page"
            >
                Fasoor
            </strong>
            </nav>

          <div className="fasoor-guide-hero-emblem">
            ♣
          </div>

          <p className="fasoor-guide-eyebrow">
            OFFICIAL GAME GUIDE
          </p>

          <h1>
            FASOOR
          </h1>

          <div className="fasoor-guide-divider">
            <span />
            <strong>◆</strong>
            <span />
          </div>

          <p className="fasoor-guide-hero-description">
            Learn how to capture cards, make 11,
            create a Soor, collect valuable cards,
            earn the Clubs bonus, and win the match.
          </p>

          <button
            type="button"
            className="fasoor-guide-play-button"
            onClick={playFasoor}
          >
            <span>▶</span>
            Play Fasoor
          </button>

        </div>

      </section>

      {/* ================================================= */}
      {/* SECTION NAVIGATION                                */}
      {/* ================================================= */}

      <nav
        className="fasoor-guide-nav"
        aria-label="Fasoor guide sections"
      >
        <div className="fasoor-guide-nav-inner">

          <a href="#overview">
            Overview
          </a>

          <a href="#rounds">
            Rounds
          </a>

          <a href="#captures">
            Making 11
          </a>

          <a href="#face-cards">
            Face Cards
          </a>

          <a href="#jack">
            Jack
          </a>

          <a href="#soor">
            Soor
          </a>

          <a href="#special-cards">
            Special Cards
          </a>

          <a href="#clubs">
            Clubs
          </a>

          <a href="#scoring">
            Scoring
          </a>

          <a href="#winning">
            Winning
          </a>

        </div>
      </nav>

      {/* ================================================= */}
      {/* CONTENT                                           */}
      {/* ================================================= */}

      <div className="fasoor-guide-content">

        {/* ================================================= */}
        {/* 01 OVERVIEW                                       */}
        {/* ================================================= */}

        <section
          id="overview"
          className="fasoor-guide-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              01
            </span>

            <div>
              <p>
                THE BASICS
              </p>

              <h2>
                What Is Fasoor?
              </h2>
            </div>

          </div>

          <div className="fasoor-guide-overview-grid">

            <div className="fasoor-guide-prose">

              <p>
                Fasoor is a competitive two-player
                card game played with a standard
                52-card deck.
              </p>

              <p>
                Players take turns playing one card
                from their hand. Depending on the
                card played and the cards on the
                table, the player may capture cards
                and add them to their collection.
              </p>

              <p>
                Numbered cards are primarily captured
                by making <strong>11</strong>. Kings
                capture Kings, Queens capture Queens,
                and Jacks use their own special
                capture rule.
              </p>

              <p>
                Strategy comes from more than simply
                capturing cards. Players must protect
                valuable cards, collect Clubs, pursue
                the 17-point Clubs bonus, and look
                for opportunities to create a
                <strong> Soor</strong>.
              </p>

            </div>

            <aside className="fasoor-guide-target-card">

              <span className="fasoor-guide-target-crown">
                ♛
              </span>

              <small>
                WINNING TARGET
              </small>

              <strong>
                52
              </strong>

              <h3>
                Points
              </h3>

              <p>
                The match uses a 52-point winning
                threshold.
              </p>

            </aside>

          </div>

        </section>

        {/* ================================================= */}
        {/* 02 ROUND STRUCTURE                                */}
        {/* ================================================= */}

        <section
          id="rounds"
          className="fasoor-guide-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              02
            </span>

            <div>
              <p>
                GAME FLOW
              </p>

              <h2>
                One Deck Is One Round
              </h2>
            </div>

          </div>

          <div className="fasoor-guide-important">

            <span>
              IMPORTANT
            </span>

            <p>
              A Fasoor round is the
              <strong> entire 52-card deck</strong>.
              Receiving another four-card hand does
              not start another round.
            </p>

          </div>

          <div className="fasoor-guide-flow">

            <article>
              <span>1</span>

              <h3>
                Shuffle
              </h3>

              <p>
                A fresh 52-card deck begins the round.
              </p>
            </article>

            <div className="fasoor-guide-flow-arrow">
              →
            </div>

            <article>
              <span>2</span>

              <h3>
                Initial Deal
              </h3>

              <p>
                Four cards go to each player and
                four cards are placed on the table.
              </p>
            </article>

            <div className="fasoor-guide-flow-arrow">
              →
            </div>

            <article>
              <span>3</span>

              <h3>
                Next Hands
              </h3>

              <p>
                After both players use their hands,
                another four cards are dealt to each.
              </p>
            </article>

            <div className="fasoor-guide-flow-arrow">
              →
            </div>

            <article>
              <span>4</span>

              <h3>
                Deck Finished
              </h3>

              <p>
                Once the complete deck is exhausted,
                that round is finished.
              </p>
            </article>

          </div>

          <div className="fasoor-guide-round-timeline">

            <div>
              <small>
                ROUND ONE
              </small>

              <strong>
                52 Cards
              </strong>
            </div>

            <Arrow />

            <div>
              <small>
                ROUND TWO
              </small>

              <strong>
                New Deck
              </strong>
            </div>

            <Arrow />

            <div>
              <small>
                ROUND THREE
              </small>

              <strong>
                New Deck
              </strong>
            </div>

          </div>

        </section>

        {/* ================================================= */}
        {/* 03 MAKING 11                                      */}
        {/* ================================================= */}

        <section
          id="captures"
          className="fasoor-guide-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              03
            </span>

            <div>
              <p>
                CORE CAPTURE RULE
              </p>

              <h2>
                Making 11
              </h2>
            </div>

          </div>

          <p className="fasoor-guide-section-intro">
            Ace through 10 use numeric values.
            The card you play captures a table card
            or a valid combination of table cards
            when the total equals
            <strong> 11</strong>.
          </p>

          <div className="fasoor-guide-examples">

            {/* 4 + 7 */}

            <article className="fasoor-guide-example">

              <div className="fasoor-guide-example-heading">
                <span>
                  EXAMPLE 1
                </span>

                <strong>
                  4 + 7 = 11
                </strong>
              </div>

              <div className="fasoor-guide-capture">

                <div className="fasoor-guide-card-group">
                  <small>
                    YOU PLAY
                  </small>

                  <PlayingCard
                    rank="4"
                    suit="hearts"
                    highlight
                  />
                </div>

                <Arrow />

                <div className="fasoor-guide-card-group">
                  <small>
                    TABLE
                  </small>

                  <PlayingCard
                    rank="7"
                    suit="clubs"
                  />
                </div>

              </div>

              <p>
                Your 4 and the table's 7 total 11,
                so the 7 is captured.
              </p>

            </article>

            {/* 3 + 8 */}

            <article className="fasoor-guide-example">

              <div className="fasoor-guide-example-heading">
                <span>
                  EXAMPLE 2
                </span>

                <strong>
                  3 + 8 = 11
                </strong>
              </div>

              <div className="fasoor-guide-capture">

                <div className="fasoor-guide-card-group">
                  <small>
                    YOU PLAY
                  </small>

                  <PlayingCard
                    rank="3"
                    suit="spades"
                    highlight
                  />
                </div>

                <Arrow />

                <div className="fasoor-guide-card-group">
                  <small>
                    TABLE
                  </small>

                  <PlayingCard
                    rank="8"
                    suit="diamonds"
                  />
                </div>

              </div>

              <p>
                Your 3 and the table's 8 make 11,
                creating a valid capture.
              </p>

            </article>

            {/* ACE + 10 */}

            <article className="fasoor-guide-example">

              <div className="fasoor-guide-example-heading">
                <span>
                  EXAMPLE 3
                </span>

                <strong>
                  Ace + 10 = 11
                </strong>
              </div>

              <div className="fasoor-guide-capture">

                <div className="fasoor-guide-card-group">
                  <small>
                    YOU PLAY
                  </small>

                  <PlayingCard
                    rank="ace"
                    suit="spades"
                    highlight
                  />
                </div>

                <Arrow />

                <div className="fasoor-guide-card-group">
                  <small>
                    TABLE
                  </small>

                  <PlayingCard
                    rank="10"
                    suit="diamonds"
                  />
                </div>

              </div>

              <p>
                An Ace has a numeric value of 1.
                Therefore Ace + 10 makes 11.
              </p>

            </article>

            {/* MULTI CARD */}

            <article className="fasoor-guide-example">

              <div className="fasoor-guide-example-heading">
                <span>
                  COMBINATION
                </span>

                <strong>
                  5 + 2 + 4 = 11
                </strong>
              </div>

              <div className="fasoor-guide-capture">

                <div className="fasoor-guide-card-group">
                  <small>
                    YOU PLAY
                  </small>

                  <PlayingCard
                    rank="5"
                    suit="clubs"
                    highlight
                  />
                </div>

                <Arrow />

                <div className="fasoor-guide-card-group">

                  <small>
                    TABLE
                  </small>

                  <div className="fasoor-guide-card-row">

                    <PlayingCard
                      rank="2"
                      suit="hearts"
                    />

                    <Plus />

                    <PlayingCard
                      rank="4"
                      suit="diamonds"
                    />

                  </div>

                </div>

              </div>

              <p>
                The played 5 plus the table's 2 and 4
                equals 11, so both table cards can be
                captured.
              </p>

            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* 04 FACE CARDS                                     */}
        {/* ================================================= */}

        <section
          id="face-cards"
          className="fasoor-guide-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              04
            </span>

            <div>
              <p>
                FACE CARD RULES
              </p>

              <h2>
                Kings and Queens
              </h2>
            </div>

          </div>

          <div className="fasoor-guide-face-grid">

            <article className="fasoor-guide-face-rule">

              <div className="fasoor-guide-face-cards">

                <PlayingCard
                  rank="king"
                  suit="spades"
                  highlight
                />

                <Arrow />

                <PlayingCard
                  rank="king"
                  suit="hearts"
                />

              </div>

              <small>
                KING TO KING
              </small>

              <h3>
                King Captures King
              </h3>

              <p>
                A King captures matching Kings
                from the table.
              </p>

              <div className="fasoor-guide-zero-points">
                King = No individual points
              </div>

            </article>

            <article className="fasoor-guide-face-rule">

              <div className="fasoor-guide-face-cards">

                <PlayingCard
                  rank="queen"
                  suit="diamonds"
                  highlight
                />

                <Arrow />

                <PlayingCard
                  rank="queen"
                  suit="clubs"
                />

              </div>

              <small>
                QUEEN TO QUEEN
              </small>

              <h3>
                Queen Captures Queen
              </h3>

              <p>
                A Queen captures matching Queens
                from the table.
              </p>

              <div className="fasoor-guide-zero-points">
                Queen = No individual points
              </div>

            </article>

          </div>

          <div className="fasoor-guide-note">

            <strong>
              ♛
            </strong>

            <p>
              Kings and Queens do not have their own
              point values. However, a King-to-King
              or Queen-to-Queen capture can still
              create a <strong>Soor</strong> when
              the capture clears the table.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* 05 JACK                                           */}
        {/* ================================================= */}

        <section
          id="jack"
          className="fasoor-guide-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              05
            </span>

            <div>
              <p>
                SPECIAL FACE CARD
              </p>

              <h2>
                The Jack
              </h2>
            </div>

          </div>

          <div className="fasoor-guide-feature">

            <div className="fasoor-guide-feature-visual">

              <PlayingCard
                rank="jack"
                suit="clubs"
                highlight
              />

              <span className="fasoor-guide-feature-score">
                +1
              </span>

            </div>

            <div className="fasoor-guide-feature-copy">

              <span className="fasoor-guide-gold-badge">
                1 POINT
              </span>

              <h3>
                Jack Capture
              </h3>

              <p>
                The Jack has a special capture rule.
                When played, it captures eligible
                cards on the table while Kings and
                Queens remain on the table.
              </p>

              <p>
                Each collected Jack is worth
                <strong> 1 point</strong>. The Jack's
                special capture ability does not itself
                award 4 points.
              </p>

              <p>
                If there are no eligible cards for
                the Jack to capture, the Jack is
                placed on the table.
              </p>

            </div>

          </div>

        </section>

        {/* ================================================= */}
        {/* 06 SOOR                                           */}
        {/* ================================================= */}

        <section
          id="soor"
          className="fasoor-guide-section fasoor-guide-soor-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              06
            </span>

            <div>
              <p>
                SIGNATURE PLAY
              </p>

              <h2>
                Soor
              </h2>
            </div>

          </div>

          <div className="fasoor-guide-soor-banner">

            <div className="fasoor-guide-soor-score">
              <span>+</span>
              10
            </div>

            <div>
              <small>
                SOOR BONUS
              </small>

              <h3>
                Clear the Table
              </h3>

              <p>
                When a valid Fasoor capture removes
                every card remaining on the table,
                the player makes a
                <strong> Soor </strong>
                and earns
                <strong> 10 points</strong>.
              </p>
            </div>

          </div>

          <div className="fasoor-guide-soor-grid">

            <article>

              <span className="fasoor-guide-rule-tag">
                MAKE 11
              </span>

              <div className="fasoor-guide-face-cards">

                <PlayingCard
                  rank="4"
                  suit="spades"
                  highlight
                />

                <Arrow />

                <PlayingCard
                  rank="7"
                  suit="hearts"
                />

              </div>

              <h3>
                Numeric Soor
              </h3>

              <p>
                If the 7 is the last table card,
                playing the 4 captures it and clears
                the table. That is a Soor.
              </p>

            </article>

            <article>

              <span className="fasoor-guide-rule-tag">
                KING TO KING
              </span>

              <div className="fasoor-guide-face-cards">

                <PlayingCard
                  rank="king"
                  suit="clubs"
                  highlight
                />

                <Arrow />

                <PlayingCard
                  rank="king"
                  suit="diamonds"
                />

              </div>

              <h3>
                King Soor
              </h3>

              <p>
                If the King capture removes the
                final table card, the player earns
                the 10-point Soor bonus.
              </p>

            </article>

            <article>

              <span className="fasoor-guide-rule-tag">
                QUEEN TO QUEEN
              </span>

              <div className="fasoor-guide-face-cards">

                <PlayingCard
                  rank="queen"
                  suit="hearts"
                  highlight
                />

                <Arrow />

                <PlayingCard
                  rank="queen"
                  suit="spades"
                />

              </div>

              <h3>
                Queen Soor
              </h3>

              <p>
                A Queen-to-Queen capture also earns
                a Soor when it clears the table.
              </p>

            </article>

          </div>

          {/* ================================================= */}
          {/* SOOR CANCELLATION                                 */}
          {/* ================================================= */}

          <div className="fasoor-guide-important">

            <span>
              SOOR CANCELLATION
            </span>

            <p>
              Soors are tracked during the same
              <strong> 52-card round</strong>.
              If both players make a Soor, one Soor
              from each player cancels out.

              For example, one Soor against one Soor
              results in no Soor points for either
              player. If one player has two Soors
              and the opponent has one, one pair
              cancels and the first player keeps
              one Soor worth
              <strong> 10 points</strong>.
            </p>

          </div>

          {/* ================================================= */}
          {/* FINAL FOUR CARDS                                  */}
          {/* ================================================= */}

          <div className="fasoor-guide-note">

            <strong>
              🚫
            </strong>

            <p>
              A Soor cannot be earned during the
              <strong> final four played cards </strong>
              of a 52-card round. A normal capture
              may still clear the table, but it does
              not receive the 10-point Soor bonus.
            </p>

          </div>

        </section>

        {/* ================================================= */}
        {/* 07 SPECIAL SCORING CARDS                          */}
        {/* ================================================= */}

        <section
          id="special-cards"
          className="fasoor-guide-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              07
            </span>

            <div>
              <p>
                VALUABLE CARDS
              </p>

              <h2>
                Aces, 10♦ and 2♣
              </h2>
            </div>

          </div>

          <div className="fasoor-guide-special-grid">

            {/* ACE */}

            <article className="fasoor-guide-special-card">

              <div className="fasoor-guide-special-card-image">

                <PlayingCard
                  rank="ace"
                  suit="hearts"
                  highlight
                />

                <span>
                  +1
                </span>

              </div>

              <small>
                EACH ACE
              </small>

              <h3>
                Ace
              </h3>

              <p>
                Each collected Ace is worth
                <strong> 1 point</strong>.
              </p>

              <p>
                For making 11, the Ace has a
                numeric value of 1.
              </p>

            </article>

            {/* 10 DIAMONDS */}

            <article className="fasoor-guide-special-card premium">

              <div className="fasoor-guide-special-card-image">

                <PlayingCard
                  rank="10"
                  suit="diamonds"
                  highlight
                />

                <span>
                  +3
                </span>

              </div>

              <small>
                PREMIUM TEN
              </small>

              <h3>
                10 of Diamonds
              </h3>

              <p>
                The <strong>10♦</strong> is worth
                <strong> 3 points </strong>
                when collected.
              </p>

              <p>
                When the game has a choice between
                valid Ten captures, 10♦ receives
                special priority unless the 10♣
                completes the seven-Clubs bonus.
              </p>

            </article>

            {/* 2 CLUBS */}

            <article className="fasoor-guide-special-card premium">

              <div className="fasoor-guide-special-card-image">

                <PlayingCard
                  rank="2"
                  suit="clubs"
                  highlight
                />

                <span>
                  +2
                </span>

              </div>

              <small>
                VALUABLE CLUB
              </small>

              <h3>
                2 of Clubs
              </h3>

              <p>
                The <strong>2♣</strong> is worth
                <strong> 2 points </strong>
                when collected.
              </p>

              <p>
                For making 11, the 2♣ still has a
                numeric value of <strong>2</strong>.
                It also counts as one Club toward the
                seven-Clubs bonus.
              </p>

            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* 08 CLUBS                                          */}
        {/* ================================================= */}

        <section
          id="clubs"
          className="fasoor-guide-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              08
            </span>

            <div>
              <p>
                SUIT STRATEGY
              </p>

              <h2>
                Clubs Have Priority
              </h2>
            </div>

          </div>

          <div className="fasoor-guide-clubs-hero">

            <div className="fasoor-guide-club-emblem">
              ♣
            </div>

            <div>

              <span className="fasoor-guide-gold-badge">
                7 CLUBS = 17 POINTS
              </span>

              <h3>
                The Clubs Bonus
              </h3>

              <p>
                Clubs are extremely important in
                Fasoor. Collecting seven Clubs during
                the round earns a
                <strong> 17-point bonus</strong>.
              </p>

              <p>
                Because Clubs contribute toward this
                major bonus, Club cards receive
                priority when selecting between
                otherwise valid captures.
              </p>

            </div>

          </div>

          <div className="fasoor-guide-seven-clubs">

            <PlayingCard
              rank="ace"
              suit="clubs"
            />

            <PlayingCard
              rank="2"
              suit="clubs"
            />

            <PlayingCard
              rank="3"
              suit="clubs"
            />

            <PlayingCard
              rank="4"
              suit="clubs"
            />

            <PlayingCard
              rank="5"
              suit="clubs"
            />

            <PlayingCard
              rank="6"
              suit="clubs"
            />

            <PlayingCard
              rank="10"
              suit="clubs"
              highlight
            />

          </div>

          <div className="fasoor-guide-priority">

            <div className="fasoor-guide-priority-heading">

              <span>
                ♣
              </span>

              <div>
                <small>
                  CAPTURE PRIORITY
                </small>

                <h3>
                  Which Valuable Card Comes First?
                </h3>
              </div>

            </div>

            <div className="fasoor-guide-priority-list">

              <article>
                <span>
                  01
                </span>

                <div>
                  <h4>
                    10♣ Completing Seven Clubs
                  </h4>

                  <p>
                    If collecting the 10♣ brings the
                    player to seven Clubs and earns
                    the 17-point bonus, the 10♣ takes
                    priority.
                  </p>
                </div>
              </article>

              <article>
                <span>
                  02
                </span>

                <div>
                  <h4>
                    10♦
                  </h4>

                  <p>
                    If 10♣ is not completing the
                    seven-Clubs bonus, prioritize a
                    valid capture containing the 10♦
                    because it is worth 3 points.
                  </p>
                </div>
              </article>

              <article>
                <span>
                  03
                </span>

                <div>
                  <h4>
                    2♣
                  </h4>

                  <p>
                    The 2♣ is worth 2 points and also
                    counts as one Club toward the
                    seven-Clubs bonus.
                  </p>
                </div>
              </article>

              <article>
                <span>
                  04
                </span>

                <div>
                  <h4>
                    Other Clubs
                  </h4>

                  <p>
                    When the special-card conditions
                    do not decide the capture, Clubs
                    receive priority among valid
                    alternatives.
                  </p>
                </div>
              </article>

            </div>

          </div>

        </section>

        {/* ================================================= */}
        {/* 09 SCORING                                        */}
        {/* ================================================= */}

        <section
          id="scoring"
          className="fasoor-guide-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              09
            </span>

            <div>
              <p>
                SCOREBOARD
              </p>

              <h2>
                Scoring Reference
              </h2>
            </div>

          </div>

          <div className="fasoor-guide-score-grid">

            {SCORE_ITEMS.map((item) => (
              <article
                key={item.title}
                className="fasoor-guide-score-card"
              >

                <div className="fasoor-guide-score-value">
                  +{item.value}
                </div>

                <small>
                  POINTS
                </small>

                <h3>
                  {item.title}
                </h3>

                <p>
                  {item.description}
                </p>

              </article>
            ))}

          </div>

          <div className="fasoor-guide-no-score">

            <div>
              <PlayingCard
                rank="king"
                suit="spades"
              />

              <div>
                <small>
                  KING
                </small>

                <strong>
                  No individual points
                </strong>
              </div>
            </div>

            <div>
              <PlayingCard
                rank="queen"
                suit="hearts"
              />

              <div>
                <small>
                  QUEEN
                </small>

                <strong>
                  No individual points
                </strong>
              </div>
            </div>

          </div>

        </section>

        {/* ================================================= */}
        {/* 10 WINNING                                        */}
        {/* ================================================= */}

        <section
          id="winning"
          className="fasoor-guide-section"
        >

          <div className="fasoor-guide-section-header">

            <span className="fasoor-guide-section-number">
              10
            </span>

            <div>
              <p>
                THE FINISH
              </p>

              <h2>
                How the Match Is Won
              </h2>
            </div>

          </div>

          <div className="fasoor-guide-winning">

            <div className="fasoor-guide-winning-score">

              <span>
                ♛
              </span>

              <strong>
                52
              </strong>

              <small>
                WINNING SCORE
              </small>

            </div>

            <div className="fasoor-guide-winning-copy">

              <h3>
                First to 52 Wins
              </h3>

              <p>
                The winning threshold is
                <strong> 52 points</strong>.
              </p>

              <p>
                The first player whose score reaches
                <strong> 52 points or more </strong>
                wins the match immediately.
              </p>

              <p>
                The game does not wait for the
                current 52-card round to finish once
                a player reaches the winning score.
              </p>

              <p>
                If neither player reaches 52 during
                the current round, a fresh 52-card
                deck begins and both players keep
                their overall match scores.
              </p>

            </div>

          </div>

        </section>

        {/* ================================================= */}
        {/* QUICK REFERENCE                                   */}
        {/* ================================================= */}

        <section className="fasoor-guide-section">

          <div className="fasoor-guide-centered-heading">

            <p>
              QUICK REFERENCE
            </p>

            <h2>
              Remember the Table
            </h2>

            <div className="fasoor-guide-divider">
              <span />
              <strong>◆</strong>
              <span />
            </div>

          </div>

          <div className="fasoor-guide-quick-grid">

            <article>
              <strong>
                11
              </strong>

              <span>
                Numeric Capture
              </span>
            </article>

            <article>
              <strong>
                K → K
              </strong>

              <span>
                King Capture
              </span>
            </article>

            <article>
              <strong>
                Q → Q
              </strong>

              <span>
                Queen Capture
              </span>
            </article>

            <article>
              <strong>
                +10
              </strong>

              <span>
                Soor
              </span>
            </article>

            <article>
              <strong>
                +1
              </strong>

              <span>
                Jack
              </span>
            </article>

            <article>
              <strong>
                +3
              </strong>

              <span>
                10♦
              </span>
            </article>

            <article>
              <strong>
                +2
              </strong>

              <span>
                2♣
              </span>
            </article>

            <article>
              <strong>
                +1
              </strong>

              <span>
                Ace
              </span>
            </article>

            <article>
              <strong>
                +17
              </strong>

              <span>
                Seven Clubs
              </span>
            </article>

            <article>
              <strong>
                52
              </strong>

              <span>
                Winning Target
              </span>
            </article>

          </div>

        </section>

        {/* ================================================= */}
        {/* FINAL CTA                                         */}
        {/* ================================================= */}

        <section className="fasoor-guide-final">

          <div className="fasoor-guide-final-suits">
            <span>♠</span>
            <span>♥</span>
            <span>♦</span>
            <span>♣</span>
          </div>

          <small>
            P-ARENA
          </small>

          <h2>
            Ready for the Table?
          </h2>

          <p>
            Put the rules into practice and
            enter the P-ARENA.
          </p>

          <div className="fasoor-guide-final-actions">

            <button
              type="button"
              onClick={playFasoor}
            >
              ▶ Play Fasoor
            </button>

            <Link to="/">
              Back to Game House
            </Link>

          </div>

        </section>

      </div>

    </main>
  );
};

export default FasoorGuide;